import { CircleHelp, ExternalLink, LifeBuoy, Search, Send } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { toast } from "sonner";
import { Spinner } from "@/components/controls/spinner";
import { LoadingRegion } from "@/components/shared/skeletons";
import { EmptyState, ErrorState } from "@/components/shared/states";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useHelpIndex, useSendSupport } from "@/hooks/use-help";
import { track } from "@/lib/analytics/analytics";
import { helpCentreUrl, helpLanguage, searchHelp } from "@/lib/api/help";
import { useAuth } from "@/lib/auth/auth-context";
import { applyServerErrors } from "@/lib/core/form-errors";
import { repeat } from "@/lib/core/utils";

/** "Help" in the header (and the phone's More sheet): opens the help dialog. */
export function HelpButton({ variant = "icon" }: { variant?: "icon" | "row" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("help.open")}
          onClick={() => setOpen(true)}
          data-testid="help-button"
        >
          <CircleHelp />
        </Button>
      ) : (
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-base hover:bg-accent"
          onClick={() => setOpen(true)}
          data-testid="help-button"
        >
          <CircleHelp className="size-5 text-muted-foreground" /> {t("help.open")}
        </button>
      )}
      {open && <HelpDialog onOpenChange={setOpen} />}
    </>
  );
}

/**
 * Help articles from the website (searched here, opened there) and "Contact us", which mails
 * support with the page and app version. Mounted only while open.
 */
export function HelpDialog({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90dvh] overflow-y-auto sm:max-w-xl"
        data-testid="help-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t("help.title")}</DialogTitle>
          <DialogDescription>{t("help.description")}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="articles">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="articles" data-testid="help-tab-articles">
              {t("help.articles")}
            </TabsTrigger>
            <TabsTrigger value="contact" data-testid="help-tab-contact">
              {t("help.contact")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="articles" className="mt-4">
            <HelpArticles />
          </TabsContent>
          <TabsContent value="contact" className="mt-4">
            <ContactForm onSent={() => onOpenChange(false)} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function HelpArticles() {
  const { t, i18n } = useTranslation();
  const lang = helpLanguage(i18n.resolvedLanguage ?? "en");
  const index = useHelpIndex(true);
  const [query, setQuery] = useState("");
  const centre = (
    <a
      href={helpCentreUrl(lang)}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-sm underline underline-offset-4"
      data-testid="help-centre-link"
    >
      {t("help.allArticles")} <ExternalLink className="size-3.5" />
    </a>
  );

  return (
    <div className="grid gap-4">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          className="pl-9"
          placeholder={t("help.search")}
          aria-label={t("help.search")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          data-testid="help-search"
        />
      </div>
      {index.isPending ? (
        <LoadingRegion testId="help-loading">
          <div className="grid gap-3">
            {repeat(4, (i) => (
              <div key={i} className="grid gap-1.5">
                <Skeleton className="h-4 w-56" />
                <Skeleton className="h-3 w-72" />
              </div>
            ))}
          </div>
        </LoadingRegion>
      ) : index.isError ? (
        <div className="grid gap-3">
          <ErrorState
            error={index.error}
            onRetry={() => void index.refetch()}
            testId="help-error"
          />
          {centre}
        </div>
      ) : (
        (() => {
          const results = searchHelp(index.data, query, lang);
          return results.length === 0 ? (
            <EmptyState
              testId="help-empty"
              icon={<LifeBuoy className="size-8" />}
              title={t("help.noResults")}
              description={t("help.noResultsHint")}
            />
          ) : (
            <ul className="grid gap-1" data-testid="help-results">
              {results.map((article) => (
                <li key={article.url}>
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-lg px-3 py-2 hover:bg-accent"
                    onClick={() => track("help_article_opened", { key: article.key })}
                  >
                    <span className="font-medium">{article.title}</span>
                    <span className="block text-sm text-muted-foreground">
                      {article.description}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          );
        })()
      )}
      {!index.isError && centre}
    </div>
  );
}

function ContactForm({ onSent }: { onSent: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const send = useSendSupport();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<{ subject?: string; message?: string }>({});

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const missing = {
      subject: subject.trim() ? undefined : t("help.required"),
      message: message.trim() ? undefined : t("help.required"),
    };
    setErrors(missing);
    if (missing.subject || missing.message) return;
    try {
      await send.mutateAsync({ subject, message, page: location.pathname });
      toast.success(t("help.sent", { email: user?.email ?? "" }));
      track("support_contacted");
      onSent();
    } catch (error) {
      const left = applyServerErrors(
        error,
        (name, err) => setErrors((current) => ({ ...current, [name]: err.message })),
        ["subject", "message"],
      );
      if (left) toast.error(left);
    }
  }

  return (
    <form
      noValidate
      onSubmit={(event) => void submit(event)}
      className="grid gap-4"
      data-testid="contact-form"
    >
      <p className="text-sm text-muted-foreground">
        {t("help.contactHint", { email: user?.email ?? "" })}
      </p>
      <div className="grid gap-1.5">
        <Label htmlFor="support-subject">{t("help.subject")}</Label>
        <Input
          id="support-subject"
          value={subject}
          maxLength={200}
          onChange={(event) => setSubject(event.target.value)}
          aria-invalid={Boolean(errors.subject)}
          data-testid="support-subject"
        />
        {errors.subject && (
          <p className="text-xs text-destructive" role="alert">
            {errors.subject}
          </p>
        )}
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="support-message">{t("help.message")}</Label>
        <Textarea
          id="support-message"
          rows={6}
          maxLength={10000}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          aria-invalid={Boolean(errors.message)}
          data-testid="support-message"
        />
        {errors.message && (
          <p className="text-xs text-destructive" role="alert">
            {errors.message}
          </p>
        )}
      </div>
      <Button
        type="submit"
        disabled={send.isPending}
        className="justify-self-end"
        data-testid="support-submit"
      >
        {send.isPending ? <Spinner /> : <Send />} {t("help.send")}
      </Button>
    </form>
  );
}
